import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Lock, Check } from "lucide-react";
import { OPENING_DATE } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const Countdown = ({ diff, labels }) => {
  const cells = [
    [Math.floor(diff / 86400000), labels.d],
    [Math.floor(diff / 3600000) % 24, labels.h],
    [Math.floor(diff / 60000) % 60, labels.m],
    [Math.floor(diff / 1000) % 60, labels.s],
  ];
  return (
    <div className="mt-6 flex flex-wrap gap-3" data-testid="opening-countdown">
      {cells.map(([v, l]) => (
        <div key={l} className="min-w-[68px] rounded-xl border-2 border-branddark/50 px-3 py-2 text-center">
          <span className="block font-display text-2xl font-extrabold tabular-nums text-navy">
            {String(v).padStart(2, "0")}
          </span>
          <span className="block font-mono text-[9px] tracking-[0.25em] text-ink/50">{l}</span>
        </div>
      ))}
    </div>
  );
};

const CodeBox = ({ codeLabel, code, note, dark, light, codeTestId }) => (
  <>
    <button
      className={`mt-6 inline-flex -rotate-1 cursor-copy items-center gap-4 rounded-xl border-2 px-6 py-3.5 transition-transform duration-300 hover:rotate-0 ${
        dark || light ? "border-branddark" : "border-navy"
      }`}
      data-testid={codeTestId}
      onClick={() => navigator.clipboard?.writeText(code)}
      title={code}
    >
      <span className={`font-mono text-[10px] tracking-[0.3em] uppercase ${dark ? "text-brand/60" : light ? "text-branddark/70" : "text-navy/60"}`}>
        {codeLabel}
      </span>
      <span className={`font-mono text-xl font-medium tracking-[0.2em] ${dark || light ? "text-branddark" : ""}`}>{code}</span>
    </button>
    <p className={`mt-3 font-mono text-[10px] tracking-[0.25em] uppercase ${dark ? "text-cream/40" : light ? "text-ink/40" : "text-navy/50"}`}>{note}</p>
  </>
);

const Band = ({ p, dark, light, testId, codeTestId }) => {
  const numRef = useRef(null);
  const numInView = useInView(numRef, { once: true, margin: "-60px" });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!p.countdown) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [p.countdown]);

  const diff = p.countdown ? Math.max(0, new Date(OPENING_DATE).getTime() - now) : null;
  const locked = p.countdown ? diff > 0 : true;
  const big = p.countdown && !locked ? p.offer.big : p.big;
  const line = p.countdown && !locked ? p.offer.line : p.line;
  const body = p.countdown && !locked ? p.offer.body : p.body;

  return (
    <div
      className={`relative overflow-hidden ${
        dark ? "bg-navy text-cream" : light ? "border-y border-black/10 bg-paper text-navy" : "bg-brand text-navy"
      }`}
      data-testid={testId}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-14 md:flex-row md:items-center md:justify-between md:px-8 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className={`mb-3 font-mono text-[11px] tracking-[0.3em] uppercase ${dark ? "text-cream/50" : light ? "text-ink/50" : "text-navy/60"}`}>
            {p.kicker}
          </p>
          <h2 className="font-display leading-[0.9] tracking-tight">
            <span className="block overflow-hidden" ref={numRef}>
              <motion.span
                className={`block text-[16vw] font-extrabold will-change-transform sm:text-[12vw] lg:text-[6.5vw] ${dark || light ? "text-branddark" : ""}`}
                initial={{ y: "110%" }}
                animate={numInView ? { y: 0 } : { y: "110%" }}
                transition={{ duration: 1, ease: EASE, delay: 0.1 }}
              >
                {big}
              </motion.span>
            </span>
            <span className="mt-3 block text-sm font-semibold uppercase tracking-[0.25em] md:text-base">
              {line}
            </span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          className="max-w-sm"
        >
          <p className={`text-base leading-relaxed ${dark ? "text-cream/70" : light ? "text-ink/70" : "text-navy/80"}`}>{body}</p>

          {p.countdown && locked && (
            <>
              <Countdown diff={diff} labels={p.countdownLabels} />
              <p
                className="mt-5 inline-flex items-center gap-2.5 rounded-xl border-2 border-dashed border-branddark/50 px-4 py-3 text-sm font-medium text-ink/70"
                data-testid="opening-locked-teaser"
              >
                <Lock className="h-4 w-4 shrink-0 text-branddark" /> {p.offer.locked}
              </p>
              <p className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-ink/40">{p.note}</p>
            </>
          )}

          {p.countdown && !locked && (
            <>
              <ul className="mt-6 space-y-2" data-testid="opening-perks">
                {p.offer.perks.map((perk) => (
                  <li key={perk} className="flex items-center gap-2.5 text-sm font-medium text-ink/80">
                    <Check className="h-4 w-4 shrink-0 text-branddark" /> {perk}
                  </li>
                ))}
              </ul>
              <CodeBox codeLabel={p.offer.codeLabel} code={p.offer.code} note={p.offer.note} light codeTestId="promo-code-opening" />
            </>
          )}

          {!p.countdown && p.code && (
            <CodeBox codeLabel={p.codeLabel} code={p.code} note={p.note} dark={dark} light={light} codeTestId={codeTestId} />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export const PromoBanner = ({ t }) => (
  <section data-testid="promo-section">
    <Band p={t.promoOpen} light testId="promo-band-opening" />
    <Band p={t.promo} testId="promo-band-students" codeTestId="promo-code" />
    <Band p={t.promoBday} dark testId="promo-band-birthday" codeTestId="promo-code-birthday" />
  </section>
);
